/*
 * The Jolly Clock-Out — nomination email verification (M15).
 *
 * The sibling of assets/campaign-backing-verify.js (M12C) and deliberately the
 * same shape: read `?t=`, strip it, do nothing until a human clicks, POST once,
 * map the outcomes. What differs is what success hands back.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY A PAGE AND NOT A LINK THAT JUST WORKS
 * ──────────────────────────────────────────────────────────────────────────
 * `POST /apps/jolly-clockout/nominations/verify` activates an Office, and
 * mailbox security scanners follow links in email automatically. A GET that
 * activated a nomination would let a scanner activate it on the nominator's
 * behalf. So the emailed link opens this page, and nothing happens until the
 * button is pressed. No auto-submit, no timed submit, no submit bound to an
 * event a scanner can trigger.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * TWO CREDENTIALS, BOTH HANDLED AS CREDENTIALS
 * ──────────────────────────────────────────────────────────────────────────
 * 1. The verification token from `?t=`. Read in the browser only, stripped
 *    from the visible URL at once, never rendered, stored, logged, attached to
 *    an event or put in a link. It lives in a closure for this page view.
 *
 * 2. The Office Wall `accessToken` the backend returns on success. This is the
 *    existing private-by-link share credential. It necessarily reaches the DOM,
 *    because the share link IS the deliverable — but only inside a same-origin
 *    href and a copy buffer. It is never persisted, never logged and never
 *    attached to an analytics event.
 *
 *    It is NOT proof of being the nominator. Everyone in the office ends up
 *    holding it, so nothing here may present it as owner authority.
 *
 * Thrown fetch errors are never inspected: a rejection can carry the request,
 * and the request carries the verification token.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * THE RESPONSE MATRIX IS KEYED ON accessToken, NOT ON alreadyVerified
 * ──────────────────────────────────────────────────────────────────────────
 * `alreadyVerified: true` does NOT imply "no link". The backend reissues a
 * share token when the nominator asked for a fresh link from their own address
 * and then clicked it — a live recovery link. Keying the UI on alreadyVerified
 * alone would hide the very link that path exists to deliver.
 *
 *   alreadyVerified false + token  -> confirmed, first time
 *   alreadyVerified true  + token  -> recovered, link reissued
 *   alreadyVerified true  + null   -> already confirmed, link not recoverable
 *                                     here; explain how to request a new one
 *
 * F-1/F-2 — single-use links. A replayed link answers
 * `{ ok, alreadyVerified, linkUsed: true, officeSlug }` and carries no
 * credential. It is handled before anything is read or stored: a browser that
 * already holds access to that office goes to its Wall, any other browser gets
 * the "already used" explanation. Nothing is written to `jco:v1` on that path.
 */
(function () {
  "use strict";

  /* ----------------------------------------------------------------------
   * Campaign credential store (M21).
   *
   * The SAME `jco:v1` object the Office Wall owns — same key, same shape, not
   * a second store. This page only ever MERGES into it: it reads what is
   * there, adds the office-scoped keys it has just earned, and writes the
   * whole object back. Dropping a namespace here would silently delete the
   * visitor's grants, entry edit credentials or remembered name, because the
   * Wall's own reader reconstructs the object from named keys.
   *
   * Nothing is read back out of here by this page, and no credential is ever
   * rendered, logged or put in a URL.
   * -------------------------------------------------------------------- */
  var STORE_KEY = "jco:v1";

  function mergeCampaignStore(slug, fields) {
    if (typeof slug !== "string" || !slug) return;
    if (!fields || typeof fields !== "object") return;

    try {
      var parsed = null;
      try {
        parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
      } catch (e) {
        parsed = null;
      }
      if (!parsed || typeof parsed !== "object") parsed = {};

      function bucket(name) {
        if (!parsed[name] || typeof parsed[name] !== "object") parsed[name] = {};
        return parsed[name];
      }

      /* M28 — THE PARTICIPANT GRANT.
       *
       * Written into the SAME `grants` namespace the Office Wall already reads,
       * in the same `{ token, expiresAt }` shape, and `lastOffice` is set
       * alongside it exactly as saveGrant() does. No parallel authentication is
       * introduced: on arrival the Wall's own resolveCredential() finds this
       * grant and loads in participant mode, which is what lets a coworker who
       * has never held a share link reach the Wall at all.
       *
       * A grant is READ access to one office and nothing more. It is written
       * here on its own account and never stands in for the contributor or the
       * share credential — those are separate fields below, and a response that
       * omits them leaves the visitor without those powers rather than
       * inheriting them from this one.
       *
       * `expiresAt` is null when the backend sends none; the Wall keeps a grant
       * with a null expiry and prunes only ones that have genuinely lapsed. */
      if (typeof fields.participantToken === "string" && fields.participantToken) {
        var expiresAt =
          typeof fields.participantTokenExpiresAt === "string" && fields.participantTokenExpiresAt
            ? fields.participantTokenExpiresAt
            : null;
        bucket("grants")[slug] = { token: fields.participantToken, expiresAt: expiresAt };
        parsed.lastOffice = slug;
      }

      if (typeof fields.contributorToken === "string" && fields.contributorToken) {
        /* M22 — `source` records WHICH route issued this credential.
         *
         * This page can only ever receive a nomination-sourced one, so the
         * value is observed rather than guessed, and the Office Wall uses it
         * to decide whether to offer the office-profile editor. It is not
         * authorization: the backend signs the same source into the token and
         * re-checks it on every profile write. */
        bucket("contributors")[slug] = { token: fields.contributorToken, source: "nomination" };
      }
      if (typeof fields.accessToken === "string" && fields.accessToken) {
        bucket("share")[slug] = fields.accessToken;
      }

      /* M23 — the nominator's own name, for the contribution form's prefill.
       *
       * A coworker's name reaches `contributorNames` because they typed it into
       * the backing form. A nominator never fills that form — they gave their
       * name to the NOMINATION form — so their first contribution opened with
       * an empty name field while every coworker's was prefilled.
       *
       * This is a convenience prefill and nothing more: the same namespace the
       * backing flow already writes, no identity or authorization meaning, and
       * the nominator's EMAIL is never stored, exactly as it is never stored on
       * the backing path.
       *
       * Written only when the backend actually supplies a name. The theme does
       * not derive one from the office, the backings list or the email local
       * part — a guessed name presented as the nominator's own would be worse
       * than an empty field. */
      if (typeof fields.nominatorName === "string" && fields.nominatorName.trim()) {
        bucket("contributorNames")[slug] = fields.nominatorName.trim().slice(0, 80);
      }

      /* M22 — THE NOMINATOR'S OWN BACKING.
       *
       * The backend creates one verified backing for the nominator in the same
       * transaction that confirms the nomination, so by the time this page has
       * a contributorToken that backing exists server-side. Recording it here
       * writes down a fact the backend has just established — it does not
       * create a backing, and it does not touch the count: `backingCount` is
       * read from the DTO on every Wall render and is never incremented in
       * the browser.
       *
       * Gated on the credential, not on the response merely being 200: no
       * credential means no confirmation happened on this request, and an
       * office confirmed before M22 is simply never re-verified here, so it
       * keeps whatever state it already had rather than gaining a tick its
       * backing count would contradict. */
      if (fields.nominatorBacking === true) {
        bucket("backed")[slug] = "verified";
      }

      window.localStorage.setItem(STORE_KEY, JSON.stringify(parsed));
    } catch (e) {
      /* Storage can be unavailable or full. The visitor simply has to reopen
       * the emailed link on the device where they want to contribute, which
       * is the documented fallback — never a thrown error on a success page. */
    }
  }

  var VERIFY_URL = "/apps/jolly-clockout/nominations/verify";
  var TOKEN_PARAM = "t";

  /* The Office Wall page path is hardcoded for the same reason
   * assets/campaign-office-wall.js hardcodes its proxy base: a merchant-editable
   * destination would let the Theme Editor point a URL carrying the Office share
   * credential at an arbitrary host. If the Office Wall page handle ever changes,
   * this constant changes with it. */
  var WALL_PATH = "/pages/jolly-clock-out";

  /* Only presence is checked. The backend owns the token format, and
   * duplicating its pattern here would let a format change make this page
   * refuse links the backend would accept.
   *
   * H-1 — snippets/campaign-token-guard.liquid normally took the token out of
   * the address bar before content_for_header ran, and hands it over once.
   * `?t=` (today's links) and `#t=` (the future format) are both still read
   * here as the fallback. */
  function readToken() {
    try {
      var early =
        typeof window.__jcoTakeUrlCredentials === "function" ? window.__jcoTakeUrlCredentials() : null;
      if (early && typeof early.t === "string" && early.t) return early.t;
    } catch (e) {
      /* Fall through to the URL. */
    }
    try {
      var url = new URL(window.location.href);
      var fromQuery = url.searchParams.get(TOKEN_PARAM);
      if (fromQuery) return fromQuery;
      if (url.hash.length > 1) return new URLSearchParams(url.hash.slice(1)).get(TOKEN_PARAM) || "";
    } catch (e) {
      /* Unreadable URL: no token. */
    }
    return "";
  }

  /* F-1/F-2 — does this browser already hold a way into this office's Wall?
   * Presence only, exactly as the Office Wall itself treats storage: the
   * server stays authoritative for expiry and revocation. */
  function hasLocalAccess(slug) {
    if (typeof slug !== "string" || !slug) return false;
    try {
      var parsed = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
      if (!parsed || typeof parsed !== "object") return false;
      var grant = parsed.grants && typeof parsed.grants === "object" ? parsed.grants[slug] : null;
      if (grant && typeof grant === "object" && typeof grant.token === "string" && grant.token) return true;
      var share = parsed.share && typeof parsed.share === "object" ? parsed.share[slug] : null;
      return typeof share === "string" && !!share;
    } catch (e) {
      return false;
    }
  }

  function stripTokenFromUrl() {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      var inHash = url.hash.length > 1 && new URLSearchParams(url.hash.slice(1)).has(TOKEN_PARAM);
      if (!url.searchParams.has(TOKEN_PARAM) && !inHash) return;
      url.searchParams.delete(TOKEN_PARAM);
      if (inHash) url.hash = "";
      var query = url.searchParams.toString();
      window.history.replaceState(
        window.history.state,
        "",
        url.pathname + (query ? "?" + query : "") + url.hash
      );
    } catch (e) {
      /* An address bar that cannot be rewritten is not a reason to fail. */
    }
  }

  /* The private-by-link share URL, exactly as assets/campaign-office-wall.js
   * parses it: the slug is NOT a credential and rides in the query string where
   * it stays bookmarkable, and the raw access token rides in the FRAGMENT only,
   * so it is never sent to any server in a request line or a Referer. */
  /* Carries ONLY Shopify's preview parameters across the hop. Nothing else
   * from this page's query string travels — `t` in particular must not. */
  function withPreviewContext(href) {
    if (typeof href !== "string" || !href) return href;
    try {
      var url = new URL(href);
      var here = new URL(window.location.href);
      var preview = here.searchParams.get("preview_theme_id");
      var view = here.searchParams.get("view");
      if (preview) url.searchParams.set("preview_theme_id", preview);
      if (view) url.searchParams.set("view", "jolly-clockout");
      return url.toString();
    } catch (e) {
      return href;
    }
  }

  /* M28 — THE DESTINATION, CARRYING NO CREDENTIAL.
   *
   * Only `office` travels, which is an identifier and not a secret. The
   * participant grant, the contributor credential and the share token have all
   * been written to `jco:v1` by this point, and the Wall reads them from there
   * — so nothing private is in the address bar, the browser history, the
   * Referer of anything the Wall loads, or any analytics that scrapes URLs.
   *
   * Deliberately NOT wallUrl(): that one appends the share token as a
   * fragment, which is right for a link the nominator hands to a coworker and
   * wrong for a redirect this page performs itself. */
  function wallDestination(slug) {
    var url = new URL(WALL_PATH, window.location.origin);
    url.searchParams.set("office", slug);
    return withPreviewContext(url.toString());
  }

  function wallUrl(slug, accessToken) {
    var url = new URL(WALL_PATH, window.location.origin);
    url.searchParams.set("office", slug);
    url.hash = "access=" + encodeURIComponent(accessToken);
    return url.toString();
  }

  function readStrings(root) {
    var node = root.querySelector("[data-jnv-strings]");
    if (!node) return {};
    try {
      var parsed = JSON.parse(node.textContent || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  /* Text only — never innerHTML. The company name comes from the backend and is
   * treated as untrusted content. */
  function setMessage(el, template, company) {
    if (!el) return;
    while (el.firstChild) el.removeChild(el.firstChild);
    var parts = String(template || "").split("[company]");
    for (var i = 0; i < parts.length; i++) {
      el.appendChild(document.createTextNode(parts[i]));
      if (i < parts.length - 1) {
        var strong = document.createElement("strong");
        strong.appendChild(document.createTextNode(company || ""));
        el.appendChild(strong);
      }
    }
  }

  function setText(el, value) {
    if (el) el.textContent = value || "";
  }

  function init(root) {
    if (root.getAttribute("data-jnv-ready") === "true") return;
    root.setAttribute("data-jnv-ready", "true");

    var S = readStrings(root);
    var title = root.querySelector("[data-jnv-title]");
    var body = root.querySelector("[data-jnv-body]");
    var confirm = root.querySelector("[data-jnv-confirm]");
    var confirmLabel = root.querySelector("[data-jnv-confirm-label]");
    var retry = root.querySelector("[data-jnv-retry]");
    var status = root.querySelector("[data-jnv-status]");
    var share = root.querySelector("[data-jnv-share]");
    var shareNote = root.querySelector("[data-jnv-share-note]");
    var visit = root.querySelector("[data-jnv-visit]");
    var shareCta = root.querySelector("[data-jnv-share-cta]");
    var manual = root.querySelector("[data-jnv-manual]");
    var manualInput = root.querySelector("[data-jnv-manual-input]");

    /* The one copy of the verification credential, for this page view only. */
    var token = readToken();
    stripTokenFromUrl();

    /* Set only on success, and only for as long as this page is open. */
    var shareUrl = "";
    var busy = false;

    function focusResult(el) {
      if (!el || typeof el.focus !== "function") return;
      try {
        el.focus();
      } catch (e) {
        /* Focus is a courtesy. */
      }
    }

    function hideActions() {
      if (confirm) confirm.hidden = true;
      if (retry) retry.hidden = true;
    }

    /* A finished outcome. The confirm button is gone, so focus moves to the
     * result or a keyboard user is stranded on a removed control. */
    function settle(titleText, bodyText, company) {
      setText(title, titleText);
      setMessage(body, bodyText, company || S.companyFallback);
      hideActions();
      setText(status, "");
      focusResult(title);
    }

    /* Recoverable: the nomination was not decided, so the action stays. */
    function offerRetry(titleText, bodyText) {
      setText(title, titleText);
      setMessage(body, bodyText, "");
      if (confirm) confirm.hidden = true;
      if (retry) {
        retry.hidden = false;
        retry.removeAttribute("aria-disabled");
      }
      setText(status, "");
      focusResult(retry);
    }

    /* Success with a share link. The link is same-origin and carries the Office
     * credential in its fragment; it is built here and never by Liquid. */
    function settleWithLink(titleText, bodyText, companyName, officeSlug, accessToken) {
      shareUrl = wallUrl(officeSlug, accessToken);
      /* The nominator's OWN hop to the wall keeps an unpublished-theme preview
       * alive, so testing this flow on a DEV theme does not bounce the tester
       * onto the published one.
       *
       * Deliberately NOT added to `shareUrl`: that is the link the nominator
       * copies and sends to coworkers, and it must be the real storefront URL
       * rather than one pinned to a theme preview. */
      if (visit) visit.setAttribute("href", withPreviewContext(shareUrl));
      if (manualInput) manualInput.value = shareUrl;
      if (share) share.hidden = false;
      setText(shareNote, S.shareNote || "");
      settle(titleText, bodyText, companyName);
    }

    function setBusy(value) {
      busy = value;
      [confirm, retry].forEach(function (button) {
        if (!button) return;
        if (value) {
          button.setAttribute("aria-disabled", "true");
        } else {
          button.removeAttribute("aria-disabled");
        }
      });
      setText(confirmLabel, value ? S.submitting || "" : S.confirm || "");
      setText(status, value ? S.statusBusy || "" : "");
    }

    async function verify() {
      /* Double-submit guard. Activation is idempotent server-side, but two
       * in-flight requests would still race their responses — and only one of
       * them would carry the share token. */
      if (busy) return;
      setBusy(true);

      var response;
      try {
        response = await fetch(VERIFY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({ token: token }),
          credentials: "same-origin"
        });
      } catch (e) {
        /* `e` is deliberately not read: a fetch rejection can carry the request,
         * and the request carries the verification token. */
        setBusy(false);
        offerRetry(S.errorTitle, S.errorBody);
        return;
      }

      var payload = null;
      try {
        payload = await response.json();
      } catch (e) {
        payload = null;
      }

      setBusy(false);

      /* 422 is the backend's single "this link will never work" status, with
       * exactly two reasons. */
      if (response.status === 422) {
        var reason = payload && typeof payload === "object" ? payload.reason : null;
        if (reason === "expired") {
          settle(S.expiredTitle, S.expiredBody, "");
        } else {
          settle(S.invalidTitle, S.invalidBody, "");
        }
        return;
      }

      if (response.status === 429 || response.status === 503) {
        offerRetry(S.rateTitle, S.rateBody);
        return;
      }

      if (!response.ok || !payload || typeof payload !== "object" || payload.ok !== true) {
        /* 400, 404, 405, 5xx, an unparseable body or an unexpected shape.
         * The nomination's fate is unknown, so this stays retryable. */
        offerRetry(S.errorTitle, S.errorBody);
        return;
      }

      /* F-1/F-2 — this link was already redeemed. The response carries no
       * credential by design and nothing is read from or stored for it. Never
       * retried: the same link will never issue credentials again. */
      if (payload.linkUsed === true) {
        var usedSlug = typeof payload.officeSlug === "string" ? payload.officeSlug : "";
        if (hasLocalAccess(usedSlug)) {
          window.location.replace(wallDestination(usedSlug));
          return;
        }
        settle(S.usedTitle || S.alreadyTitle, S.usedBody || S.alreadyBody, "");
        return;
      }

      var accessToken = typeof payload.accessToken === "string" ? payload.accessToken : "";
      var slug = typeof payload.officeSlug === "string" ? payload.officeSlug : "";
      var company = typeof payload.companyName === "string" ? payload.companyName : "";

      /* Keyed on the token, not on alreadyVerified: a reissued recovery link
       * arrives as alreadyVerified true AND carries a share token. */
      /* M21 — a verified nominator earns contribution permission here, without
       * having to back their own office. The raw read-only access token is kept
       * too, so Share works from the Wall this page links to even after a
       * refresh. Both are office-scoped; a null contributorToken is not
       * treated as authorization. */
      var contributorToken =
        typeof payload.contributorToken === "string" ? payload.contributorToken : "";

      /* The backend field is read defensively across the names it could
       * reasonably use, because the documented `nominations/verify` response
       * does not yet carry one at all. Until it does this resolves to "" and
       * the field simply stays empty, exactly as it does today — see the
       * backend note in CUSTOMIZATIONS.md. */
      var nominatorName = "";
      var nameKeys = ["nominatorName", "displayName", "contributorName"];
      for (var n = 0; n < nameKeys.length; n++) {
        var candidate = payload[nameKeys[n]];
        if (typeof candidate === "string" && candidate.trim()) {
          nominatorName = candidate.trim();
          break;
        }
      }

      /* M28 — office-scoped READ access. Never treated as ownership: the
       * contributor and share credentials are read separately below, and a
       * response that omits them leaves this visitor without those powers. */
      var participantToken =
        typeof payload.participantToken === "string" ? payload.participantToken : "";

      mergeCampaignStore(slug, {
        participantToken: participantToken,
        participantTokenExpiresAt: payload.participantTokenExpiresAt,
        contributorToken: contributorToken,
        accessToken: accessToken,
        nominatorName: nominatorName,
        /* Only when this confirmation actually granted contribution
         * permission. See the note in mergeCampaignStore(). */
        nominatorBacking: !!contributorToken
      });

      /* M28 — STRAIGHT INTO THE WALL.
       *
       * The success screen is gone from the normal path: one confirmation
       * click now lands the nominator on their own Office Wall. The hop is
       * only taken when the Wall is genuinely reachable — a participant grant
       * from this response, or a share token that opens it. Redirecting
       * without either would put someone on a Wall that cannot load, which the
       * old success screen at least explained.
       *
       * The verification token was stripped from this URL before the request
       * went out, and `replace()` rather than `assign()` keeps the spent
       * verification page out of the back history. */
      var canReachWall = !!slug && (!!participantToken || !!accessToken);
      if (canReachWall) {
        window.location.replace(wallDestination(slug));
        return;
      }

      /* Verified, but this link has already handed over its credentials and
       * will not do so again, so there is nothing to carry into the Wall. Say
       * how to get a new one; disclose nothing else. */
      setBusy(false);
      settle(S.alreadyTitle, S.alreadyBody, company);
    }

    /* The last resort, shown only when neither the share sheet nor the
     * clipboard could take the link: reveal the field and select it, so the
     * nominator is never left without a way to get their own link. */
    function revealManualLink(message) {
      if (manual) manual.hidden = false;
      setText(status, message || "");
      if (!manualInput) return;
      focusResult(manualInput);
      try {
        manualInput.select();
      } catch (e) {
        /* Selection is a convenience. */
      }
    }

    /* M22 — the Office Wall's own share behaviour, step for step.
     *
     * Native sheet first, with `text` and `url` as SEPARATE fields so each
     * target app places them properly; clipboard second; the visible field
     * third. A cancelled sheet is not a failure and deliberately does NOT
     * fall through to the clipboard — that would copy something the nominator
     * has just declined to send.
     *
     * `shareUrl` is the read-only Wall link and nothing else. The verification
     * token was stripped from this page's address bar before any of this ran,
     * and it is never read back into a share payload. */
    async function shareWall() {
      if (!shareUrl) return;

      var text = S.shareMessage || "";

      if (navigator && typeof navigator.share === "function") {
        try {
          await navigator.share({ title: S.shareTitle || "", text: text, url: shareUrl });
          setText(status, "");
          return;
        } catch (e) {
          /* The thrown value is not inspected beyond its name: it can carry
           * the payload, and the payload carries the Office credential. */
          if (e && e.name === "AbortError") return;
        }
      }

      try {
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(text ? text + " " + shareUrl : shareUrl);
          setText(status, S.copied || "");
          return;
        }
      } catch (e) {
        /* Clipboard denied or unavailable. Fall through. */
      }

      revealManualLink(S.shareFailed || S.copyFailed || "");
    }

    if (!token) {
      /* No link, or a reload after the token was stripped. Nothing was
       * attempted, so this is never phrased as a failure. */
      settle(S.noTokenTitle, S.noTokenBody, "");
      return;
    }

    if (confirm) {
      confirm.hidden = false;
      confirm.addEventListener("click", verify);
    }
    if (retry) retry.addEventListener("click", verify);
    if (shareCta) shareCta.addEventListener("click", shareWall);
  }

  function boot() {
    var roots = document.querySelectorAll("[data-jnv-root]");
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The Theme Customizer re-renders a section without reloading the page. */
  document.addEventListener("shopify:section:load", boot);
})();
